import React, { useRef, useMemo } from 'react';

import { Button } from '@unfold/button';
import { Grid, GridItem } from '@unfold/grid';
import { CloseIcon } from '@unfold/icon';
import { unit, useUnit, styled } from '@unfold/theme';
import { Typography } from '@unfold/typography';
import { useScrollPosition, useResizeObserver } from '@unfold/utils';

import { modalPadding } from './constants';
import { DialogContainer, DialogBody, CloseButton } from './styled';
import { IDialogProps } from './types';
import { getScrollPositionName } from './utils';

export const Dialog: React.FC<IDialogProps> = ({
  title,
  width,
  size,
  headerActions,
  footerActions,
  customFooter,
  hasCloseButton,
  onClose,
  className,
  children,
  titleIcon,
  hasPadding,
  ...props
}) => {
  const hasHeaderActions = Boolean(headerActions);
  const hasHeader = hasHeaderActions || Boolean(title);
  const hasFooter = Boolean(footerActions);
  const hasCustomFooter = Boolean(customFooter);

  const dialogEl = useRef<HTMLDivElement>(null);
  const { y: dialogYPos } = useScrollPosition(dialogEl, 300);
  const dialogScrollPos = getScrollPositionName(dialogYPos, dialogEl.current);

  const observedElement = useResizeObserver(dialogEl);
  const dialogElScrollHeight = observedElement?.target?.scrollHeight;
  const dialogElHeight = observedElement?.contentRect?.height;
  const isScrollable = useMemo(() => {
    return (dialogElScrollHeight ?? 0) > (dialogElHeight ?? 0);
  }, [dialogElScrollHeight, dialogElHeight]);

  const FooterAndHeaderGrid = styled(Grid)`
    padding: ${modalPadding}px;
  `;

  const StyledTypography = titleIcon
    ? styled(Typography)`
        margin-left: ${unit.px(1)};
      `
    : Typography;

  const ogGridHorizontalPadding = useUnit(1);

  return (
    <>
      <DialogContainer
        className={className}
        width={width}
        size={size}
        elevation="modal"
        {...props}
      >
        {hasCloseButton && (
          <CloseButton>
            <Button
              appearance="subtle"
              onClick={onClose}
              iconBefore={<CloseIcon />}
            />
          </CloseButton>
        )}
        {hasHeader && (
          <FooterAndHeaderGrid
            horizontalPadding={ogGridHorizontalPadding}
            justify="space-between"
          >
            {title && (
              <GridItem enableEllipsis isStretch isWidthAuto>
                {titleIcon && titleIcon}
                <StyledTypography noWrap variant="H600">
                  {title}
                </StyledTypography>
              </GridItem>
            )}
            {headerActions && <GridItem isWidthAuto>{headerActions}</GridItem>}
          </FooterAndHeaderGrid>
        )}
        <DialogBody
          ref={dialogEl}
          hasHeader={hasHeader}
          hasFooter={hasFooter}
          hasTopBorder={
            isScrollable &&
            hasHeader &&
            (dialogScrollPos == 'bottom' || dialogScrollPos === 'middle')
          }
          hasBottomBorder={
            isScrollable &&
            hasFooter &&
            (dialogScrollPos === 'top' || dialogScrollPos === 'middle')
          }
          hasPadding={hasPadding}
        >
          {children}
        </DialogBody>
        {hasFooter || hasCustomFooter ? (
          <FooterAndHeaderGrid
            horizontalPadding={ogGridHorizontalPadding}
            justify={hasCustomFooter ? 'flex-start' : 'flex-end'}
          >
            <GridItem
              justifyContent={hasCustomFooter ? 'flex-start' : 'flex-end'}
              isStretch={hasCustomFooter}
            >
              {footerActions ?? customFooter}
            </GridItem>
          </FooterAndHeaderGrid>
        ) : null}
      </DialogContainer>
    </>
  );
};
